import {
    type StateColorType,
    type StateColorKey,
    STATE_COLOR_KEYS
} from "./state";

export type TypeColorVariant = "filled" | "outline" | "tonal" | "ghost";

export type ContentContainerColorRoleType = {
    key: keyof NCoreUIKit.ContainerContentColors;
    group: "content.container";
};

export type ContentBorderColorRoleType = {
    key: keyof NCoreUIKit.BorderContentColors;
    group: "content.border";
};

export type ContentTextColorRoleType = {
    key: keyof NCoreUIKit.TextContentColors;
    group: "content.text";
};

export type ContentIconColorRoleType = {
    key: keyof NCoreUIKit.IconContentColors;
    group: "content.icon";
};

export type SystemLowColorRoleType = {
    key: keyof NCoreUIKit.LowColors;
    group: "system.low";
};

export type SystemOnContainerColorRoleType = {
    key: keyof NCoreUIKit.SystemColorPalette["onContainer"];
    group: "system.onContainer";
};

export type SystemOnColorRoleType = {
    key: keyof NCoreUIKit.SystemColorPalette["on"];
    group: "system.on";
};

export type SystemDisabledOverlayColorRoleType = {
    key: keyof NCoreUIKit.StateSystemColors["overlay"]["disabled"];
    group: "system.state.overlay.disabled";
};

export type SystemFillOverlayColorRoleType = {
    key: keyof NCoreUIKit.StateSystemColors["overlay"]["fill"];
    group: "system.state.overlay.fill";
};

export type SystemDisabledBorderColorRoleType = {
    key: keyof NCoreUIKit.StateSystemColors["border"]["disabled"];
    group: "system.state.border.disabled";
};

export type SystemDisabledContentColorRoleType = {
    key: keyof NCoreUIKit.StateSystemColors["content"]["disabled"];
    group: "system.state.content.disabled";
};

export type ContainerColorRole = ContentContainerColorRoleType | SystemDisabledOverlayColorRoleType | SystemFillOverlayColorRoleType | SystemLowColorRoleType | "transparent";

export type TitleColorRole = ContentTextColorRoleType | SystemDisabledContentColorRoleType | SystemOnContainerColorRoleType | SystemOnColorRoleType;

export type IconColorRole = ContentIconColorRoleType | SystemDisabledContentColorRoleType | SystemOnContainerColorRoleType | SystemOnColorRoleType;

export type BorderColorRole = ContentBorderColorRoleType | SystemDisabledBorderColorRoleType | "transparent";

export type ColorRole = ContainerColorRole | BorderColorRole | TitleColorRole | IconColorRole;

export type TypeColorRolesType = {
    containerColor: ContainerColorRole;
    borderColor: BorderColorRole;
    overlayColor: StateColorKey;
    titleColor: TitleColorRole;
    iconColor: IconColorRole;
};

export type TypeColorsType = {
    overlayColor: StateColorKey;
    containerColor: string;
    borderColor: string;
    titleColor: string;
    iconColor: string;
};

export type ColorRoleValueConstantType = {
    colors: NCoreUIKit.ActivePalette["colors"];
    role: ColorRole;
};

export type TypeColorsConstantType = {
    colors: NCoreUIKit.ActivePalette["colors"];
    variant: TypeColorVariant;
    type: StateColorType;
    isSelected?: boolean;
    isDisabled?: boolean;
};

export type TypeColorRolesConstantType = {
    variant: TypeColorVariant;
    type: StateColorType;
    isSelected?: boolean;
    isDisabled?: boolean;
};

export const TYPE_COLOR_ROLES: Record<StateColorType, Record<TypeColorVariant, TypeColorRolesType>> = {
    neutral: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.neutral,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "high"
            },
            iconColor: {
                group: "content.icon",
                key: "high"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.neutral,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "default"
            },
            titleColor: {
                group: "content.text",
                key: "high"
            },
            iconColor: {
                group: "content.icon",
                key: "high"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.neutral,
            containerColor: {
                group: "system.state.overlay.fill",
                key: STATE_COLOR_KEYS.neutral
            },
            titleColor: {
                key: STATE_COLOR_KEYS.neutral,
                group: "system.onContainer"
            },
            iconColor: {
                key: STATE_COLOR_KEYS.neutral,
                group: "system.onContainer"
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "neutral"
            },
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "onPrimary"
            },
            iconColor: {
                group: "content.icon",
                key: "onPrimary"
            },
            overlayColor: "default"
        }
    },
    primary: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.primary,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "emphasized"
            },
            iconColor: {
                group: "content.icon",
                key: "emphasized"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.primary,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "emphasized"
            },
            titleColor: {
                group: "content.text",
                key: "emphasized"
            },
            iconColor: {
                group: "content.icon",
                key: "emphasized"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.primary,
            titleColor: {
                key: STATE_COLOR_KEYS.primary,
                group: "system.onContainer"
            },
            iconColor: {
                key: STATE_COLOR_KEYS.primary,
                group: "system.onContainer"
            },
            containerColor: {
                key: STATE_COLOR_KEYS.primary,
                group: "system.low"
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "primary"
            },
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "onPrimary"
            },
            iconColor: {
                group: "content.icon",
                key: "onPrimary"
            },
            overlayColor: "default"
        }
    },
    success: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.success,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "success"
            },
            iconColor: {
                group: "content.icon",
                key: "success"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.success,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "success"
            },
            titleColor: {
                group: "content.text",
                key: "success"
            },
            iconColor: {
                group: "content.icon",
                key: "success"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.success,
            titleColor: {
                key: STATE_COLOR_KEYS.success,
                group: "system.onContainer"
            },
            iconColor: {
                key: STATE_COLOR_KEYS.success,
                group: "system.onContainer"
            },
            containerColor: {
                key: STATE_COLOR_KEYS.success,
                group: "system.low"
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "success"
            },
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "onPrimary"
            },
            iconColor: {
                group: "content.icon",
                key: "onPrimary"
            },
            overlayColor: "default"
        }
    },
    warning: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.warning,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "warning"
            },
            iconColor: {
                group: "content.icon",
                key: "warning"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.warning,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "warning"
            },
            titleColor: {
                group: "content.text",
                key: "warning"
            },
            iconColor: {
                group: "content.icon",
                key: "warning"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.warning,
            titleColor: {
                key: STATE_COLOR_KEYS.warning,
                group: "system.onContainer"
            },
            iconColor: {
                key: STATE_COLOR_KEYS.warning,
                group: "system.onContainer"
            },
            containerColor: {
                key: STATE_COLOR_KEYS.warning,
                group: "system.low"
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "warning"
            },
            borderColor: "transparent",
            titleColor: {
                group: "system.on",
                key: "warning"
            },
            iconColor: {
                group: "system.on",
                key: "warning"
            },
            overlayColor: "default"
        }
    },
    danger: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.danger,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "danger"
            },
            iconColor: {
                group: "content.icon",
                key: "danger"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.danger,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "danger"
            },
            titleColor: {
                group: "content.text",
                key: "danger"
            },
            iconColor: {
                group: "content.icon",
                key: "danger"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.danger,
            containerColor: {
                key: STATE_COLOR_KEYS.danger,
                group: "system.low"
            },
            titleColor: {
                group: "system.onContainer",
                key: STATE_COLOR_KEYS.danger
            },
            iconColor: {
                group: "system.onContainer",
                key: STATE_COLOR_KEYS.danger
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "danger"
            },
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "onPrimary"
            },
            iconColor: {
                group: "content.icon",
                key: "onPrimary"
            },
            overlayColor: "default"
        }
    },
    info: {
        ghost: {
            overlayColor: STATE_COLOR_KEYS.info,
            containerColor: "transparent",
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "info"
            },
            iconColor: {
                group: "content.icon",
                key: "info"
            }
        },
        outline: {
            overlayColor: STATE_COLOR_KEYS.info,
            containerColor: {
                group: "content.container",
                key: "raised"
            },
            borderColor: {
                group: "content.border",
                key: "info"
            },
            titleColor: {
                group: "content.text",
                key: "info"
            },
            iconColor: {
                group: "content.icon",
                key: "info"
            }
        },
        tonal: {
            overlayColor: STATE_COLOR_KEYS.info,
            titleColor: {
                group: "system.onContainer",
                key: STATE_COLOR_KEYS.info
            },
            iconColor: {
                group: "system.onContainer",
                key: STATE_COLOR_KEYS.info
            },
            containerColor: {
                key: STATE_COLOR_KEYS.info,
                group: "system.low"
            },
            borderColor: "transparent"
        },
        filled: {
            containerColor: {
                group: "content.container",
                key: "info"
            },
            borderColor: "transparent",
            titleColor: {
                group: "content.text",
                key: "onPrimary"
            },
            iconColor: {
                group: "content.icon",
                key: "onPrimary"
            },
            overlayColor: "default"
        }
    }
};

export const SELECTED_COLOR_ROLES: TypeColorRolesType = {
    containerColor: {
        group: "content.container",
        key: "selected"
    },
    borderColor: {
        group: "content.border",
        key: "default"
    },
    titleColor: {
        group: "content.text",
        key: "high"
    },
    iconColor: {
        group: "content.icon",
        key: "high"
    },
    overlayColor: "default"
};

export const DISABLED_COLOR_ROLES: Record<TypeColorVariant, TypeColorRolesType> = {
    filled: {
        containerColor: {
            group: "system.state.overlay.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        titleColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        iconColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        overlayColor: STATE_COLOR_KEYS.neutral,
        borderColor: "transparent"
    },
    ghost: {
        titleColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        iconColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        overlayColor: STATE_COLOR_KEYS.neutral,
        containerColor: "transparent",
        borderColor: "transparent"
    },
    outline: {
        titleColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        iconColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        borderColor: {
            group: "system.state.border.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        overlayColor: STATE_COLOR_KEYS.neutral,
        containerColor: "transparent"
    },
    tonal: {
        containerColor: {
            group: "system.state.overlay.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        titleColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        iconColor: {
            group: "system.state.content.disabled",
            key: STATE_COLOR_KEYS.neutral
        },
        overlayColor: STATE_COLOR_KEYS.neutral,
        borderColor: "transparent"
    }
};

export const getColorRoleValue = ({
    colors,
    role
}: ColorRoleValueConstantType): string => {
    if(role === "transparent") {
        return role;
    }

    if(role.group === "content.container") {
        return colors.content.container[role.key];
    }

    if(role.group === "content.border") {
        return colors.content.border[role.key];
    }

    if(role.group === "content.text") {
        return colors.content.text[role.key];
    }

    if(role.group === "content.icon") {
        return colors.content.icon[role.key];
    }

    if(role.group === "system.low") {
        return colors.system.low[role.key];
    }

    if(role.group === "system.state.overlay.disabled") {
        return colors.system.state.overlay.disabled[role.key];
    }

    if(role.group === "system.state.overlay.fill") {
        return colors.system.state.overlay.fill[role.key];
    }

    if(role.group === "system.state.border.disabled") {
        return colors.system.state.border.disabled[role.key];
    }

    if(role.group === "system.state.content.disabled") {
        return colors.system.state.content.disabled[role.key];
    }

    if(role.group === "system.on") {
        return colors.system.on[role.key];
    }

    return colors.system.onContainer[role.key];
};

export const getTypeColorRoles = ({
    isDisabled = false,
    isSelected = false,
    variant,
    type
}: TypeColorRolesConstantType): TypeColorRolesType => {
    if(isDisabled) {
        return DISABLED_COLOR_ROLES[variant];
    }

    if(isSelected) {
        return SELECTED_COLOR_ROLES;
    }

    return TYPE_COLOR_ROLES[type][variant];
};

export const getTypeColors = ({
    isDisabled = false,
    isSelected = false,
    variant,
    colors,
    type
}: TypeColorsConstantType): TypeColorsType => {
    const currentRoles = getTypeColorRoles({
        isDisabled,
        isSelected,
        variant,
        type
    });

    return {
        overlayColor: currentRoles.overlayColor,
        containerColor: getColorRoleValue({
            role: currentRoles.containerColor,
            colors
        }),
        borderColor: getColorRoleValue({
            role: currentRoles.borderColor,
            colors
        }),
        titleColor: getColorRoleValue({
            role: currentRoles.titleColor,
            colors
        }),
        iconColor: getColorRoleValue({
            role: currentRoles.iconColor,
            colors
        })
    };
};
